import { injectable, decorate, inject } from 'inversify';
import 'reflect-metadata';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';
import type ApiService from './ApiService';
import type {
  AddContractorPayload,
  CheckinCoordinates,
  GeoObject,
  GeoObjectPayload,
  GeoObjectPoint,
  MapComment,
  MapContractor,
  MapDocument,
  MapEntityId,
  MapFilters,
  MapPhoto,
  MapStage,
  MeetingObject,
  MeetingObjectPayload,
  SalesOrgCoordinates,
  SelectOption,
  UpdateStagePayload,
} from 'types/map';

interface ApiItemsResponse<T> {
  data: {
    data: {
      items: T[];
    };
  };
}

interface ApiDataResponse<T> {
  data: {
    data: {
      data: T;
    };
  };
}

const getItems = <T>(response: unknown): T[] => {
  return (response as ApiItemsResponse<T>).data.data.items;
};

class MapService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  async getGeoObjects(
    params: MapFilters,
    axiosOptions: Record<string, unknown> = {},
  ): Promise<GeoObjectPoint[]> {
    const result = await this.apiService.get(
      '/map/geo_objects',
      params as unknown as Record<string, unknown>,
      axiosOptions,
    );

    return getItems<GeoObjectPoint>(result);
  }

  async addMeetingObject(data: MeetingObjectPayload): Promise<MeetingObject> {
    const result = await this.apiService.post(
      '/map/new_meeting_objects',
      data as unknown as Record<string, unknown>,
    );

    return getItems<MeetingObject>(result)[0];
  }

  async getMeetingObject(id: MapEntityId): Promise<MeetingObject> {
    const result = await this.apiService.get(`/map/new_meeting_objects/${id}`);

    return getItems<MeetingObject>(result)[0];
  }

  async addGeoObjects(data: GeoObjectPayload): Promise<GeoObject> {
    const transformedData = {
      ...data,
      floorsCount: data.floorsCount || 0,
      squareFloor: data.squareFloor || 0,
      squareRoof: data.squareRoof || 0,
      squareOuterWalls: data.squareOuterWalls || 0,
      squareInnerWalls: data.squareInnerWalls || 0,
      phasesNumber: data.phasesNumber || 0,
      undergroundFloorsCount: data.undergroundFloorsCount || 0,
    };
    const result = await this.apiService.post(
      '/map/geo_objects',
      transformedData,
    );

    return getItems<GeoObject>(result)[0];
  }

  async editGeoObjects(
    geoObjectId: MapEntityId,
    data: GeoObjectPayload,
  ): Promise<GeoObject> {
    const transformedData = {
      ...data,
      floorsCount: data.floorsCount || 0,
      squareFloor: data.squareFloor || 0,
      squareRoof: data.squareRoof || 0,
      squareOuterWalls: data.squareOuterWalls || 0,
      squareInnerWalls: data.squareInnerWalls || 0,
      phasesNumber: data.phasesNumber || 0,
      undergroundFloorsCount: data.undergroundFloorsCount || 0,
    };
    const result = await this.apiService.put(
      `/map/geo_objects/${geoObjectId}`,
      transformedData,
    );

    return getItems<GeoObject>(result)[0];
  }

  async getRegions(params: MapFilters = {}): Promise<SelectOption[]> {
    const result = await this.apiService.get(
      '/map/geo_objects/regions',
      params as unknown as Record<string, unknown>,
    );

    return getItems<SelectOption>(result);
  }

  async getGeoObject(geoObjectId: MapEntityId): Promise<GeoObject> {
    const result = await this.apiService.get(`/map/geo_objects/${geoObjectId}`);

    return getItems<GeoObject>(result)[0];
  }

  async getPhotos(
    geoObjectId: MapEntityId,
    abortSignal?: AbortSignal,
  ): Promise<MapPhoto[]> {
    const result = await this.apiService.get(
      `/map/geo_objects/${geoObjectId}/photos`,
      undefined,
      { signal: abortSignal },
    );

    return getItems<MapPhoto>(result);
  }

  async uploadPhoto(geoObjectId: MapEntityId, files: File[]): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append(file.name, file, file.name));

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    await this.apiService.post(
      `/map/geo_objects/${geoObjectId}/photos`,
      formData as unknown as Record<string, unknown>,
      config,
    );
  }

  async getDocuments(
    geoObjectId: MapEntityId,
    abortSignal?: AbortSignal,
  ): Promise<MapDocument[]> {
    const result = await this.apiService.get(
      `/map/geo_objects/${geoObjectId}/documents`,
      undefined,
      { signal: abortSignal },
    );

    return getItems<MapDocument>(result);
  }

  async uploadDocument(
    geoObjectId: MapEntityId,
    files: File[],
  ): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append(file.name, file, file.name));

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    await this.apiService.post(
      `/map/geo_objects/${geoObjectId}/documents`,
      formData as unknown as Record<string, unknown>,
      config,
    );
  }

  async getStages(geoObjectId: MapEntityId): Promise<MapStage[]> {
    const result = await this.apiService.get(
      `/map/geo_objects/${geoObjectId}/stages`,
    );

    return getItems<MapStage>(result);
  }

  async updateStage(
    { geoObjectId, stageId }: { geoObjectId: MapEntityId; stageId: MapEntityId },
    formData: UpdateStagePayload,
  ): Promise<MapStage> {
    const result = await this.apiService.put(
      `/map/geo_objects/${geoObjectId}/stages/${stageId}`,
      formData,
    );

    return getItems<MapStage>(result)[0];
  }

  async getComments(geoObjectId: MapEntityId): Promise<MapComment[]> {
    const result = await this.apiService.get(
      `/map/geo_objects/${geoObjectId}/comments`,
    );

    return getItems<MapComment>(result);
  }

  async createComment(
    geoObjectId: MapEntityId,
    comment: string,
  ): Promise<MapComment[]> {
    const result = await this.apiService.post(
      `/map/geo_objects/${geoObjectId}/comments`,
      {
        content: comment,
      },
    );

    return getItems<MapComment>(result);
  }

  async getContractors(geoObjectId: MapEntityId): Promise<MapContractor[]> {
    const result = await this.apiService.get(
      `/map/geo_objects/${geoObjectId}/contractors`,
    );

    return getItems<MapContractor>(result);
  }

  async addContractor(
    geoObjectId: MapEntityId,
    formData: AddContractorPayload,
  ): Promise<MapContractor[]> {
    const result = await this.apiService.post(
      `/map/geo_objects/${geoObjectId}/contractors`,
      formData,
    );

    return getItems<MapContractor>(result);
  }

  async checkin(
    geoObjectId: MapEntityId,
    coordinates: CheckinCoordinates,
  ): Promise<string> {
    const result = await this.apiService.post(
      `/map/geo_objects/${geoObjectId}/checkin`,
      coordinates as unknown as Record<string, unknown>,
    );

    return (result as ApiDataResponse<string>).data.data.data;
  }

  async getCoordsBySalesOrgId(): Promise<SalesOrgCoordinates[]> {
    const result = await this.apiService.get(`/map/sales_org_coords`);

    return getItems<SalesOrgCoordinates>(result);
  }
}

(decorate as (decorator: ClassDecorator, target: unknown) => void)(
  injectable() as ClassDecorator,
  MapService,
);

(decorate as (
  decorator: ParameterDecorator,
  target: unknown,
  parameterIndex: number,
) => void)(
  inject(SERVICE_IDENTIFIER.ApiService) as ParameterDecorator,
  MapService,
  0,
);

export default MapService;
